import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';

import { ChannelIcon } from '@/components/channel-icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { ChannelInput, NotificationChannel } from '@/lib/settings';
import { cn } from '@/lib/utils';

interface NotificationChannelDialogProps {
  channels: NotificationChannel[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (type: string, input: ChannelInput) => void | Promise<void>;
}

/**
 * Lists every notification channel and swaps to a per-channel form when one is
 * selected. Secret fields are never prefilled: they render blank and "leave
 * blank to keep" preserves the stored value.
 */
export function NotificationChannelDialog({
  channels,
  open,
  onOpenChange,
  onSubmit,
}: NotificationChannelDialogProps) {
  const [selected, setSelected] = useState<NotificationChannel | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!selected) return;
    setEnabled(selected.enabled);
    const initial: Record<string, string> = {};
    for (const field of selected.fields) {
      initial[field.key] = field.secret ? '' : field.value;
    }
    setValues(initial);
  }, [selected]);

  useEffect(() => {
    if (!open) setSelected(null);
  }, [open]);

  const storedSecret = (key: string) =>
    Boolean(selected?.fields.find((field) => field.key === key)?.value);
  const invalid =
    enabled &&
    Boolean(
      selected?.fields.some(
        (field) =>
          field.required &&
          !values[field.key]?.trim() &&
          !(field.secret && storedSecret(field.key)),
      ),
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {selected ? (
          <form
            className="grid gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (invalid) return;
              onSubmit(selected.type, { enabled, values });
              setSelected(null);
            }}
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="-ml-2 size-7"
                  aria-label="Back to channels"
                  onClick={() => setSelected(null)}
                >
                  <ArrowLeft className="size-4" />
                </Button>
                <ChannelIcon type={selected.type} className="size-4" />
                {selected.label}
              </DialogTitle>
              <DialogDescription>{selected.description}</DialogDescription>
            </DialogHeader>

            <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
              <div className="grid gap-0.5">
                <span className="font-medium text-sm">Enabled</span>
                <span className="text-muted-foreground text-xs">
                  Send notifications to this channel.
                </span>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} />
            </div>

            {selected.fields.map((field) => (
              <div key={field.key} className="grid gap-2">
                <Label htmlFor={`channel-${selected.type}-${field.key}`}>{field.label}</Label>
                <Input
                  id={`channel-${selected.type}-${field.key}`}
                  type={field.secret ? 'password' : 'text'}
                  autoComplete="off"
                  value={values[field.key] ?? ''}
                  placeholder={
                    field.secret && storedSecret(field.key)
                      ? '•••••••• (leave blank to keep)'
                      : field.placeholder
                  }
                  onChange={(event) =>
                    setValues((prev) => ({ ...prev, [field.key]: event.target.value }))
                  }
                />
                {field.secret && storedSecret(field.key) && (
                  <p className="text-muted-foreground text-xs">
                    A {field.label.toLowerCase()} is already saved. Leave blank to keep it.
                  </p>
                )}
              </div>
            ))}

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setSelected(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={invalid}>
                Save
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="grid gap-4">
            <DialogHeader>
              <DialogTitle>Notifications</DialogTitle>
              <DialogDescription>Channels the platform sends admin alerts to.</DialogDescription>
            </DialogHeader>

            <div className="grid gap-2">
              {channels.map((channel) => (
                <div
                  key={channel.type}
                  className="flex items-center justify-between gap-3 rounded-lg border p-3"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <ChannelIcon type={channel.type} className="size-5 shrink-0" />
                    <div className="grid gap-0.5">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{channel.label}</p>
                        <Badge
                          variant="outline"
                          className={cn(
                            'border-transparent',
                            channel.enabled
                              ? 'bg-emerald-500/10 text-emerald-600'
                              : 'bg-red-500/10 text-red-600',
                          )}
                        >
                          {channel.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground text-xs">{channel.description}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    onClick={() => setSelected(channel)}
                  >
                    Configure
                  </Button>
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
